import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import flatpickr from 'flatpickr'
import { French } from 'flatpickr/dist/l10n/fr'
import { formatDateParam } from '../../utils/dateHelpers'

const pad = number => String(number).padStart(2, '0')

const parseIso = value => {
  if (!value)
    return null
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/* eslint-disable camelcase */
const FLATPICKR_OPTIONS = {
  date: { dateFormat: 'd/m/Y' },
  datetime: { dateFormat: 'd/m/Y H:i', enableTime: true, time_24hr: true },
  time: { dateFormat: 'H:i', enableTime: true, noCalendar: true, time_24hr: true }
}
/* eslint-enable camelcase */

const formatValue = (date, mode) => {
  if (!date)
    return null
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`
  if (mode === 'time')
    return time
  if (mode === 'datetime')
    return `${formatDateParam(date)} ${time}`
  return formatDateParam(date)
}

const parseValue = (value, mode) => {
  if (!value)
    return null
  if (mode === 'time') {
    const [hours, minutes] = value.split(':').map(Number)
    return new Date(2000, 0, 1, hours, minutes)
  }
  const [datePart, timePart] = value.split(' ')
  const [year, month, day] = datePart.split('-').map(Number)
  const [hours, minutes] = (timePart || '00:00').split(':').map(Number)
  return new Date(year, month - 1, day, hours, minutes)
}

const ICON_CLASSES = 'tw:absolute tw:left-3 tw:top-1/2 '
  + 'tw:-translate-y-1/2 tw:pointer-events-none tw:text-ds-neutral-highest tw:text-xs'

const CLEAR_BUTTON_CLASSES = 'tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 '
  + 'tw:flex tw:items-center tw:justify-center tw:w-6 tw:h-6 tw:bg-transparent '
  + 'tw:border-0 tw:p-0 tw:cursor-pointer tw:text-gray-500 '
  + 'tw:hover:text-ds-neutral-highest'

const INPUT_CLASSES = 'tw:block tw:w-full tw:bg-white tw:text-sm tw:text-ds-neutral-highest '
  + 'tw:border tw:border-solid tw:border-gray-300 tw:rounded! tw:pl-8 tw:pr-8 tw:py-2.5 '
  + 'tw:focus:outline-none tw:focus:border-ds-primary!'

const DateInput = ({ className, clearable, maxDate, minDate, mode, onChange, placeholder, value }) => {
  const inputRef = useRef(null)
  const instanceRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    const instance = flatpickr(inputRef.current, {
      locale: French,
      ...FLATPICKR_OPTIONS[mode],
      onChange: ([date]) => onChangeRef.current(formatValue(date, mode))
    })
    instanceRef.current = instance
    return () => {
      instance.destroy()
      instanceRef.current = null
    }
  }, [mode])

  useEffect(() => {
    instanceRef.current?.set('minDate', parseIso(minDate))
    instanceRef.current?.set('maxDate', parseIso(maxDate))
  }, [minDate, maxDate])

  useEffect(() => {
    const parsed = parseValue(value, mode)
    if (!parsed) {
      instanceRef.current?.clear()
      return
    }
    instanceRef.current?.setDate(parsed, false)
    instanceRef.current?.jumpToDate(parsed)
  }, [value, mode])

  const handleClear = useCallback(event => {
    event.stopPropagation()
    onChangeRef.current(null)
  }, [])

  const iconClass = mode === 'time' ? 'fas fa-clock' : 'fas fa-calendar-alt'

  return (
    <div className={`tw:relative tw:grow ${className}`.trim()}>
      <i className={`${iconClass} ${ICON_CLASSES}`} />
      <input
        ref={inputRef}
        className={INPUT_CLASSES}
        placeholder={placeholder}
        type="text"
        readOnly
      />
      {clearable && value ? (
        <button
          aria-label="Effacer la valeur"
          className={CLEAR_BUTTON_CLASSES}
          type="button"
          onClick={handleClear}
        >
          <i className="fas fa-times" />
        </button>
      ) : null}
    </div>
  )
}

DateInput.propTypes = {
  className: PropTypes.string,
  clearable: PropTypes.bool,
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
  mode: PropTypes.oneOf(['date', 'datetime', 'time']),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string
}

DateInput.defaultProps = {
  className: '',
  clearable: true,
  maxDate: null,
  minDate: null,
  mode: 'date',
  placeholder: '',
  value: null
}

export default DateInput
